// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: where each reader's avatar belongs. The server keeps one row per
// (channel, user) — a high-water mark, not a row per message — so "seen" is
// always inferred from "has read up to message M". Repeating a face on every
// message at or before M would stamp it down the whole backlog, so each reader
// is anchored to a single message.
//
// Two placements:
// - 'own' (DMs): the newest of the current user's messages the reader has
//   read. Deliberately not an exact match on M: the moment the other person
//   replies, their mark moves onto their own message, and an exact match would
//   pull the avatar off a message they demonstrably read.
// - 'all' (group DMs and server channels): the newest message the reader has
//   read, whoever wrote it, so the faces show who has caught up with the
//   conversation rather than who has read the current user's last message.

import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {MessageTypes} from '@fluxer/constants/src/ChannelConstants';

export type SeenAnchorMode = 'own' | 'all';

/** Snowflake comparison; malformed ids sort as "older than everything". */
function isAtOrBefore(candidate: string, mark: string): boolean {
	try {
		return BigInt(candidate) <= BigInt(mark);
	} catch {
		return false;
	}
}

/**
 * Message id -> the readers whose avatar belongs on it.
 *
 * @param messages the channel's loaded messages, oldest first
 * @param receipts userId -> the last message id that user has read
 * @param currentUserId never shown as a reader; in 'own' mode only this
 *   author's messages carry avatars
 * @param options.hasMoreAfter the loaded window stops short of the newest
 *   message, so a mark past its end belongs to a message that is not on
 *   screen and must not pile onto the window's last one
 */
export function buildSeenAnchors(
	messages: ReadonlyArray<Message>,
	receipts: ReadonlyMap<string, string> | undefined,
	currentUserId: string | null,
	options: {mode?: SeenAnchorMode; hasMoreAfter?: boolean} = {},
): Map<string, Array<string>> {
	const {mode = 'own', hasMoreAfter = false} = options;
	const anchors = new Map<string, Array<string>>();
	if (!receipts || receipts.size === 0 || !currentUserId) return anchors;
	const shown = messages.filter((message) => message.type !== MessageTypes.CLIENT_SYSTEM);
	const candidates = mode === 'own' ? shown.filter((message) => message.author.id === currentUserId) : shown;
	if (candidates.length === 0) return anchors;
	const newestLoaded = messages.at(-1)?.id;
	for (const [userId, readMessageId] of receipts) {
		if (userId === currentUserId) continue;
		if (hasMoreAfter && newestLoaded != null && !isAtOrBefore(readMessageId, newestLoaded)) continue;
		let anchor: string | null = null;
		for (let index = candidates.length - 1; index >= 0; index--) {
			const candidate = candidates[index]!.id;
			if (isAtOrBefore(candidate, readMessageId)) {
				anchor = candidate;
				break;
			}
		}
		if (anchor === null) continue;
		const readers = anchors.get(anchor);
		if (readers) {
			readers.push(userId);
		} else {
			anchors.set(anchor, [userId]);
		}
	}
	return anchors;
}
